import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import {
  Code, Cloud, Database, Shield, Smartphone, Globe,
  Mail, Phone, MapPin, ArrowRight, CheckCircle, Users,
  TrendingUp, Server, Lock, Zap, Menu, X, ChevronRight,
  Award, Clock, ShieldCheck, MessageSquare, FileText,
  Calendar, Download, Linkedin, Twitter, Github, Star
} from 'lucide-react';

function Portfolio() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [scrolled, setScrolled] = useState(false);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const testimonialIntervalRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Testimonial slider auto-play
  useEffect(() => {
    const testimonials = [
      {
        name: "Alex Johnson",
        company: "CEO, TechCorp",
        text: "KANDAX transformed our entire infrastructure. Their solutions saved us 40% in operational costs.",
        rating: 5
      },
      {
        name: "Maria Garcia",
        company: "CTO, GlobalBank",
        text: "Outstanding cybersecurity implementation. Our systems have never been more secure.",
        rating: 5
      },
      {
        name: "Robert Chen",
        company: "Director, RetailCo",
        text: "The e-commerce platform they built handles millions of transactions flawlessly. Highly recommended!",
        rating: 5
      }
    ];

    if (testimonials.length > 1) {
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
      }

      testimonialIntervalRef.current = setInterval(() => {
        setCurrentTestimonialIndex(prevIndex =>
          prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);

      return () => {
        if (testimonialIntervalRef.current) {
          clearInterval(testimonialIntervalRef.current);
        }
      };
    }
  }, []);

  const services = [
    {
      icon: <Cloud size={40} />,
      title: "Cloud Solutions",
      description: "Secure and scalable cloud infrastructure with 24/7 monitoring and support.",
      features: ["AWS/Azure/GCP", "Cloud Migration", "DevOps", "Kubernetes"],
      color: "from-blue-500/10 to-red-500/5"
    },
    {
      icon: <Code size={40} />,
      title: "Custom Software",
      description: "Tailored software solutions that drive business growth and efficiency.",
      features: ["Web Applications", "Mobile Apps", "Enterprise Software", "API Development"],
      color: "from-red-500/10 to-blue-500/5"
    },
    {
      icon: <Database size={40} />,
      title: "Data Analytics",
      description: "Transform your data into actionable insights with advanced analytics.",
      features: ["Big Data", "Business Intelligence", "Machine Learning", "Data Visualization"],
      color: "from-green-500/10 to-red-500/5"
    },
    {
      icon: <Shield size={40} />,
      title: "Cybersecurity",
      description: "Comprehensive security solutions to protect your digital assets.",
      features: ["Network Security", "Penetration Testing", "Compliance", "Threat Monitoring"],
      color: "from-red-500/10 to-purple-500/5"
    },
    {
      icon: <Smartphone size={40} />,
      title: "Mobile Development",
      description: "Native and cross-platform mobile applications for iOS and Android.",
      features: ["React Native", "Flutter", "iOS Development", "Android Development"],
      color: "from-purple-500/10 to-red-500/5"
    },
    {
      icon: <Globe size={40} />,
      title: "Web Development",
      description: "Modern, responsive websites and web applications with cutting-edge tech.",
      features: ["React/Next.js", "Node.js", "Progressive Web Apps", "E-commerce"],
      color: "from-red-500/10 to-orange-500/5"
    }
  ];

  const projects = [
    {
      id: 1,
      title: "Enterprise Banking System",
      client: "Global Bank Inc.",
      description: "Complete digital transformation of legacy banking systems with enhanced security.",
      tech: ["React", "Node.js", "Microservices", "AWS"],
      results: ["40% Reduction in Costs", "99.9% Uptime", "3x Performance"],
    },
    {
      id: 2,
      title: "Healthcare Platform",
      client: "MediCare Solutions",
      description: "HIPAA-compliant patient management system with telemedicine capabilities.",
      tech: ["React Native", "Python", "PostgreSQL", "Docker"],
      results: ["50% Efficiency Gain", "24/7 Availability", "Full Compliance"],
    },
    {
      id: 3,
      title: "E-commerce Platform",
      client: "Retail Giant Corp",
      description: "Scalable e-commerce platform handling 1M+ monthly transactions.",
      tech: ["Next.js", "TypeScript", "MongoDB", "Kubernetes"],
      results: ["200% Revenue Growth", "2s Load Time", "Zero Downtime"],
    },
    {
      id: 4,
      title: "IoT Smart City Solution",
      client: "City Municipal Corp",
      description: "City-wide IoT implementation for smart traffic and energy management.",
      tech: ["Python", "TensorFlow", "AWS IoT", "React"],
      results: ["30% Energy Saved", "Real-time Analytics", "100K+ Devices"],
    }
  ];

  const clients = [
    { name: "TechCorp", logo: "TC", since: "2018" },
    { name: "GlobalBank", logo: "GB", since: "2019" },
    { name: "MediCare", logo: "MC", since: "2020" },
    { name: "RetailCo", logo: "RC", since: "2021" },
    { name: "AutoDrive", logo: "AD", since: "2020" },
    { name: "EduTech", logo: "ET", since: "2022" }
  ];

  const stats = [
    { value: "150+", label: "Projects Completed", icon: <CheckCircle />, suffix: "" },
    { value: "98%", label: "Client Satisfaction", icon: <TrendingUp />, suffix: "" },
    { value: "50+", label: "Expert Team Members", icon: <Users />, suffix: "" },
    { value: "24/7", label: "Support Available", icon: <Server />, suffix: "" }
  ];

  const team = [
    {
      name: "Sarah Chen",
      role: "CTO & Lead Architect",
      expertise: "Cloud & DevOps",
      image: "SC",
      social: { linkedin: "#", twitter: "#", github: "#" }
    },
    {
      name: "Michael Rodriguez",
      role: "Head of Development",
      expertise: "Full Stack",
      image: "MR",
      social: { linkedin: "#", twitter: "#", github: "#" }
    },
    {
      name: "Priya Sharma",
      role: "Security Director",
      expertise: "Cybersecurity",
      image: "PS",
      social: { linkedin: "#", twitter: "#", github: "#" }
    },
    {
      name: "David Kim",
      role: "AI/ML Lead",
      expertise: "Data Science",
      image: "DK",
      social: { linkedin: "#", twitter: "#", github: "#" }
    }
  ];

  const testimonials = [
    {
      name: "Alex Johnson",
      company: "CEO, TechCorp",
      text: "KANDAX transformed our entire infrastructure. Their solutions saved us 40% in operational costs.",
      rating: 5
    },
    {
      name: "Maria Garcia",
      company: "CTO, GlobalBank",
      text: "Outstanding cybersecurity implementation. Our systems have never been more secure.",
      rating: 5
    },
    {
      name: "Robert Chen",
      company: "Director, RetailCo",
      text: "The e-commerce platform they built handles millions of transactions flawlessly. Highly recommended!",
      rating: 5
    }
  ];

  const navItems = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "Projects", href: "#projects" },
    { name: "About", href: "#about" },
    { name: "Team", href: "#team" },
    { name: "Testimonials", href: "#testimonials" },
    { name: "Contact", href: "#contact" }
  ];

  const projectCategories = ["all", "finance", "healthcare", "retail", "iot"];

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={16}
          className={i <= rating ? "text-red-500 fill-red-500" : "text-gray-300"}
        />
      );
    }
    return stars;
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonialIndex(index);
    if (testimonialIntervalRef.current) {
      clearInterval(testimonialIntervalRef.current);
      testimonialIntervalRef.current = setInterval(() => {
        setCurrentTestimonialIndex(prevIndex =>
          prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans scroll-smooth">

      {/* Hero Section - Full Width Banner */}
      <section
        id="home"
        className="relative h-[60vh] md:h-[70vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: "url('/Portfolio.png')" }}
      >
       
      </section>

      {/* Main Content in Container Fluid */}
      <div className="container-fluid px-4 md:px-6 lg:px-8">
        
        {/* Clients Section */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-white to-gray-50 border-t border-gray-100">
          <div className="px-0">
            <div className="text-center mb-8 md:mb-12">
              <h3 className="text-gray-500 mb-2 text-sm md:text-base">Trusted by Industry Leaders</h3>
              <div className="w-24 h-0.5 bg-red-600 mx-auto"></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {clients.map((client, index) => (
                <div
                  key={index}
                  className="group p-4 bg-white rounded-xl border border-gray-200 hover:border-red-300 transition-all duration-300 hover:scale-105 shadow-sm hover:shadow-lg"
                >
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-red-600 mb-2">
                      {client.logo}
                    </div>
                    <div className="text-gray-800 font-medium mb-1">{client.name}</div>
                    <div className="text-gray-500 text-xs">Since {client.since}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-16 md:py-20 bg-white">
          <div className="px-0">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                Focused Services. <span className="text-red-600">Clear Outcomes</span>.
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                End-to-end technology solutions with measurable business impact
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`group relative p-6 md:p-8 rounded-2xl border-2 transition-all duration-500 cursor-pointer transform hover:-translate-y-2 ${activeService === index
                    ? 'border-red-600 shadow-2xl shadow-red-500/10 bg-white'
                    : 'border-gray-200 hover:border-red-300 bg-white'
                    }`}
                  onMouseEnter={() => setActiveService(index)}
                >
                  <div className={`absolute inset-0 ${service.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  <div className="relative">
                    <div className="text-red-600 mb-6 transform group-hover:scale-110 transition-transform duration-300">
                      {service.icon}
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-sm md:text-base">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <a href="#contact" className="inline-flex items-center text-red-600 hover:text-red-700 text-sm font-medium">
                        Learn More <ChevronRight size={16} className="ml-1" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="px-0">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900">
                  What Makes <span className="text-red-600">KANDAX</span> Different
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                  With over a decade of experience in digital transformation, we've helped
                  hundreds of businesses migrate to modern technology stacks, improving
                  efficiency, security, and scalability.
                </p>
                <div className="space-y-6">
                  {[
                    { icon: <Shield />, title: "Enterprise-Grade Security", desc: "Military-grade security protocols for all our solutions" },
                    { icon: <Zap />, title: "24/7 Monitoring", desc: "Round-the-clock support and system monitoring" },
                    { icon: <TrendingUp />, title: "Scalable Solutions", desc: "Architecture designed to grow with your business" },
                    { icon: <Award />, title: "Award-Winning Team", desc: "Industry-recognized experts in their fields" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 group">
                      <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-red-100 transition-all duration-300">
                        {React.cloneElement(item.icon, { className: "text-red-600 group-hover:scale-110 transition-transform" })}
                      </div>
                      <div>
                        <h4 className="font-bold mb-1 text-lg text-gray-900">{item.title}</h4>
                        <p className="text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-6 md:p-8 border border-red-100 shadow-lg">
                  <div className="grid grid-cols-2 gap-4 md:gap-6">
                    {[
                      { value: "10+", label: "Years Experience", color: "text-red-600" },
                      { value: "50+", label: "Certified Experts", color: "text-red-600" },
                      { value: "24/7", label: "Support", color: "text-red-600" },
                      { value: "99.9%", label: "Uptime SLA", color: "text-red-600" }
                    ].map((stat, index) => (
                      <div key={index} className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 group hover:border-red-300 transition-all duration-300">
                        <div className={`${stat.color} text-2xl md:text-3xl font-bold mb-2 group-hover:scale-110 transition-transform inline-block`}>
                          {stat.value}
                        </div>
                        <div className="text-gray-700 text-sm md:text-base">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 md:mt-8 p-4 md:p-6 bg-white rounded-xl border border-red-100">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
                        <Clock className="text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">Ready to Start?</h4>
                        <p className="text-gray-600">Get your free consultation today</p>
                      </div>
                      <Link
                        to="/contact"
                        className="ml-auto px-4 py-2 bg-red-600 text-white font-bold rounded-lg
                 hover:bg-red-700 transition shadow-lg hover:shadow-red-200"
                      >
                        Contact Now
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="py-16 md:py-20 bg-white">
          <div className="px-0">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                Real-world solutions delivering <span className="text-red-600">measurable business impact</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                See how we've helped businesses transform digitally
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {projects
                .filter(project => activeTab === 'all' || project.category === activeTab)
                .map((project) => (
                  <div
                    key={project.id}
                    className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-red-300 transition-all duration-500 hover:-translate-y-1 shadow-lg hover:shadow-2xl"
                  >
                    <div className="p-6 md:p-8">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold mb-2 text-gray-900">{project.title}</h3>
                          <p className="text-gray-500 text-sm mb-2">Client: {project.client}</p>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-6 leading-relaxed">{project.description}</p>

                      <div className="mb-6">
                        <h4 className="font-bold mb-3 text-gray-900">Technologies Used</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.tech.map((tech, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg text-sm hover:border-red-300 transition"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        {project.results.map((result, i) => (
                          <div key={i} className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
                            <CheckCircle size={16} className="text-red-600 mx-auto mb-2" />
                            <span className="text-sm text-gray-700">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section id="team" className="py-16 md:py-20 bg-white">
          <div className="px-0">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900">
                Leadership <span className="text-red-600">Team</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Meet the experts driving innovation at KANDAX
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {team.map((member, index) => (
                <div
                  key={index}
                  className="group bg-white p-6 rounded-2xl border border-gray-200 hover:border-red-300 transition-all duration-500 hover:-translate-y-2 shadow-lg hover:shadow-2xl"
                >
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-red-100 transition-all">
                      <div className="text-2xl font-bold text-red-600">
                        {member.image}
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">{member.name}</h3>
                  <div className="text-red-600 mb-3 text-sm font-medium">{member.role}</div>
                  <div className="text-gray-600 text-sm mb-6">{member.expertise}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

export default Portfolio;